/**
 * FieldCert — Logo Component System (Web)
 * ────────────────────────────────────────
 * Single source of truth for all logo marks.
 * Import from this file everywhere — never recreate the mark inline.
 *
 * Brand canon: brand-guidelines v1.0 (April 2026)
 * SVG geometry: svg-export.html Mark 1a / Mark 2a
 *
 * ─── WHEN TO USE EACH ───────────────────────────────────────
 *
 *  <HexMark />      Primary brand mark. Use on:
 *                   Sign-in screen, dashboard header, documents,
 *                   certificates, marketing, web.
 *
 *  <SquareMark />   Gold square app icon. Use on:
 *                   iOS/Android home screen, App Store, notifications,
 *                   favicon. DO NOT use hex mark in these contexts.
 *
 *  <Wordmark />     Text lockup. Use standalone on:
 *                   Loading screens, splash, email headers.
 *
 *  <HexLockup />    Hex mark + Wordmark combined. Use on:
 *                   Sign-in header, nav bar, dashboard top-left.
 *                   This is the standard in-app lockup.
 */

export const BrandColors = {
  goldPrimary:   '#d9a441', // check-rules:ignore — brand canon, single source of truth for the FieldCert logo
  goldLight:     '#f2cc72', // check-rules:ignore — brand canon
  goldDark:      '#b8832a', // check-rules:ignore — brand canon
  goldAccent:    '#e8b84a', // check-rules:ignore — brand canon
  goldSubtle:    'rgba(217,164,65,0.45)',
  navy:          '#070d1a', // check-rules:ignore — brand canon
  surface:       '#0f1627', // check-rules:ignore — brand canon
  elevated:      '#151f34', // check-rules:ignore — brand canon
  textPrimary:   '#edf2fb', // check-rules:ignore — brand canon
  textSecondary: '#9aa9c3', // check-rules:ignore — brand canon
  textMuted:     '#6b7891', // check-rules:ignore — brand canon
  border:        '#1c2740', // check-rules:ignore — brand canon
  borderMid:     '#2a3856', // check-rules:ignore — brand canon
};

// Fixed polygon coordinates per brand spec (svg-export.html Mark 1a).
// All points are in a 0 0 100 100 viewBox — do not recompute.
const HEX_OUTER = '50,4 93,27.5 93,72.5 50,96 7,72.5 7,27.5';
const HEX_INNER_1 = '50,13 84,32 84,68 50,87 16,68 16,32';
const HEX_INNER_2 = '50,21 77,37 77,63 50,79 23,63 23,37';

/**
 * Resolve the mark's surface treatment to CSS-variable colours.
 *
 * - `'auto'` (default): follows the active `data-theme`, so the mark can never
 *   be wrong by omission — this is what fixes the invisible light-theme auth logo.
 * - `'dark'` / `'light'`: force a treatment for a surface that does not follow
 *   the theme (e.g. the always-dark sidebar / login brand panel).
 *
 * Colours live as tokens in `styles/_tokens.css`, never hardcoded here.
 */
function resolveSurface(surface, light) {
  // `light` is a deprecated boolean alias kept for existing callers:
  // true → light surface, false → dark surface. Prefer `surface`.
  const s = surface || (light === true ? 'light' : light === false ? 'dark' : 'auto');
  const suffix = s === 'dark' ? '-on-dark' : s === 'light' ? '-on-light' : '';
  return {
    hexBg:   `var(--fc-logo-hex-bg${suffix})`,
    hexText: `var(--fc-logo-hex-text${suffix})`,
    text:    `var(--fc-logo-text${suffix})`,
    tagline: `var(--fc-logo-tagline${suffix})`,
  };
}

/**
 * HexMark — Primary brand mark (Mark 1)
 *
 * @param {number}  size    - Icon size in px. Default: 38
 * @param {'auto'|'dark'|'light'} surface - Surface treatment. Default: 'auto' (follows theme).
 * @param {boolean} light   - Deprecated boolean alias for `surface` (true=light, false=dark).
 */
export function HexMark({ size = 38, surface, light }) {
  const { hexBg: bg, hexText: textColor } = resolveSurface(surface, light);
  const variantKey = surface || (light ? 'l' : 'd');
  const gradId    = `hg-${size}-${variantKey}`;
  const gradId2   = `hg2-${size}-${variantKey}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden="true"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <defs>
        <linearGradient id={gradId} x1="15" y1="8" x2="85" y2="92" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#f2cc72" /> {/* check-rules:ignore — brand canon */}
          <stop offset="35%"  stopColor="#d9a441" /> {/* check-rules:ignore — brand canon */}
          <stop offset="65%"  stopColor="#b8832a" /> {/* check-rules:ignore — brand canon */}
          <stop offset="100%" stopColor="#e8b84a" /> {/* check-rules:ignore — brand canon */}
        </linearGradient>
        <linearGradient id={gradId2} x1="15" y1="8" x2="85" y2="92" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#f2cc72" stopOpacity="0.45" /> {/* check-rules:ignore — brand canon */}
          <stop offset="100%" stopColor="#b8832a" stopOpacity="0.45" /> {/* check-rules:ignore — brand canon */}
        </linearGradient>
      </defs>

      <polygon points={HEX_OUTER} fill={bg} />
      <polygon points={HEX_OUTER} fill="none" stroke={`url(#${gradId})`} strokeWidth="4.5" />
      <polygon points={HEX_INNER_1} fill="none" stroke={`url(#${gradId2})`} strokeWidth="2" />
      <polygon points={HEX_INNER_2} fill="none" stroke={`url(#${gradId2})`} strokeWidth="1.2" opacity="0.55" />

      <text
        x="50"
        y="58"
        textAnchor="middle"
        fontFamily="'DM Sans', sans-serif"
        fontWeight="900"
        fontSize="26"
        fill={textColor}
        letterSpacing="-1.5"
      >
        FC
      </text>
    </svg>
  );
}

/**
 * SquareMark — Gold square app icon (Mark 2)
 *
 * Use ONLY for: iOS/Android icon, App Store, favicon, notifications.
 * Do NOT use the hex mark in these contexts (hex clips in iOS rounded-square mask).
 *
 * @param {number}  size  - Icon size in px. Default: 38
 * @param {boolean} light - No-op (icon is always gold fill). Kept for API parity.
 */
export function SquareMark({ size = 38 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden="true"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <rect width="100" height="100" rx="22" fill="#d9a441" /> {/* check-rules:ignore — brand canon */}
      <rect x="11" y="11" width="78" height="78" rx="12"
        fill="none" stroke="#070d1a" strokeWidth="2.5" opacity="0.3" /> {/* check-rules:ignore — brand canon */}
      {/* Corner registration marks */}
      <line x1="11" y1="24" x2="11" y2="11" stroke="#070d1a" strokeWidth="4" strokeLinecap="square" /> {/* check-rules:ignore — brand canon */}
      <line x1="11" y1="11" x2="24" y2="11" stroke="#070d1a" strokeWidth="4" strokeLinecap="square" /> {/* check-rules:ignore — brand canon */}
      <line x1="89" y1="24" x2="89" y2="11" stroke="#070d1a" strokeWidth="4" strokeLinecap="square" /> {/* check-rules:ignore — brand canon */}
      <line x1="89" y1="11" x2="76" y2="11" stroke="#070d1a" strokeWidth="4" strokeLinecap="square" /> {/* check-rules:ignore — brand canon */}
      <line x1="11" y1="76" x2="11" y2="89" stroke="#070d1a" strokeWidth="4" strokeLinecap="square" /> {/* check-rules:ignore — brand canon */}
      <line x1="11" y1="89" x2="24" y2="89" stroke="#070d1a" strokeWidth="4" strokeLinecap="square" /> {/* check-rules:ignore — brand canon */}
      <line x1="89" y1="76" x2="89" y2="89" stroke="#070d1a" strokeWidth="4" strokeLinecap="square" /> {/* check-rules:ignore — brand canon */}
      <line x1="89" y1="89" x2="76" y2="89" stroke="#070d1a" strokeWidth="4" strokeLinecap="square" /> {/* check-rules:ignore — brand canon */}
      <text
        x="50"
        y="64"
        textAnchor="middle"
        fontFamily="'DM Sans', sans-serif"
        fontWeight="900"
        fontSize="36"
        fill="#070d1a" // check-rules:ignore — brand canon
        letterSpacing="-2"
      >
        FC
      </text>
    </svg>
  );
}

const WORDMARK_SCALES = {
  xs: { fs: 16, sub: 8,  gap: 2 },
  sm: { fs: 20, sub: 9,  gap: 3 },
  md: { fs: 28, sub: 10, gap: 5 },
  lg: { fs: 38, sub: 12, gap: 6 },
  xl: { fs: 52, sub: 15, gap: 8 },
};

/**
 * Wordmark — "FieldCert" text + "Asset Compliance" tagline
 *
 * @param {'xs'|'sm'|'md'|'lg'|'xl'} size  - Text size preset. Default: 'md'
 * @param {'auto'|'dark'|'light'}     surface - Surface treatment. Default: 'auto' (follows theme).
 * @param {boolean}                   light - Deprecated boolean alias for `surface`.
 */
export function Wordmark({ size = 'md', surface, light }) {
  const s = WORDMARK_SCALES[size] || WORDMARK_SCALES.md;
  const { text, tagline } = resolveSurface(surface, light);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: s.gap }}>
      <div
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: 900,
          fontSize: s.fs,
          letterSpacing: '-0.025em',
          lineHeight: 1,
          color: text,
          whiteSpace: 'nowrap',
        }}
      >
        Field<span style={{ color: BrandColors.goldPrimary }}>Cert</span>
      </div>
      <div
        style={{
          fontFamily: "'DM Mono', monospace",
          fontWeight: 500,
          fontSize: s.sub,
          letterSpacing: '0.2em',
          color: tagline,
          textTransform: 'uppercase',
          lineHeight: 1,
        }}
      >
        Asset Compliance
      </div>
    </div>
  );
}

/**
 * HexLockup — Hex mark + Wordmark side by side
 *
 * Standard in-app lockup for sign-in header, nav bar, dashboard.
 *
 * @param {number}  iconSize - HexMark size. Default: 38
 * @param {string}  wmSize   - Wordmark size preset. Default: 'sm'
 * @param {number}  gap      - Space between mark and wordmark. Default: 10
 * @param {'auto'|'dark'|'light'} surface - Surface treatment. Default: 'auto' (follows theme).
 * @param {boolean} light    - Deprecated boolean alias for `surface`.
 */
export function HexLockup({ iconSize = 38, wmSize = 'sm', gap = 10, surface, light }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap }}>
      <HexMark size={iconSize} surface={surface} light={light} />
      <Wordmark size={wmSize} surface={surface} light={light} />
    </div>
  );
}
